import { useState, useEffect, useRef, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import agent from '../../../app/api/agent'
import { useLegalText } from '../../../app/hooks/useLegalText'
import type { SlopPostDto, SlopPostCommentDto } from '../../../app/models/slopPost'
import type { FirearmModel } from '../../../app/models/firearmModel'
import { store } from '../../../app/stores/store'
import { MAX_SLOP_VIDEO_BYTES } from '../slopFeedConstants'

export interface UseSlopLibraryFeedOptions {
  /** When false, skip paged library fetch (Discover / embed admin routes). Default true. */
  loadLibraryFeed?: boolean
}

export function useSlopLibraryFeed (options: UseSlopLibraryFeedOptions = {}) {
  const loadLibraryFeed = options.loadLibraryFeed ?? true
  const isAdmin = store.userStore.user?.isAdmin ?? false
  const isLoggedIn = !!store.userStore.user
  const history = useHistory()

  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [consentGiven, setConsentGiven] = useState(false)

  const [firearmModels, setFirearmModels] = useState<FirearmModel[]>([])
  const [cartridgeOptions, setCartridgeOptions] = useState<{ value: string; label: string }[]>([])
  const [firearmListLoading, setFirearmListLoading] = useState(false)
  const [firearmSearch, setFirearmSearch] = useState('')
  const [selectedFirearmModelId, setSelectedFirearmModelId] = useState<string | null>(null)
  const [selectedCartridgeSpecId, setSelectedCartridgeSpecId] = useState<string | null>(null)

  const [embedUrl, setEmbedUrl] = useState('')
  const [embedTitle, setEmbedTitle] = useState('')
  const [embedDescription, setEmbedDescription] = useState('')
  const [embedLoading, setEmbedLoading] = useState(false)
  const [embedError, setEmbedError] = useState<string | null>(null)

  const [feedPosts, setFeedPosts] = useState<SlopPostDto[]>([])
  const [feedLoading, setFeedLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  const [selectedPost, setSelectedPost] = useState<SlopPostDto | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const [comments, setComments] = useState<SlopPostCommentDto[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [commentSubmitting, setCommentSubmitting] = useState(false)
  const [replyingToId, setReplyingToId] = useState<number | null>(null)
  const [replyText, setReplyText] = useState('')
  const [shareCopied, setShareCopied] = useState(false)

  const { text: consentText, loading: consentLoading } = useLegalText('slop-feed-consent')
  const sentinelRef = useRef<HTMLDivElement>(null)

  const loadInitialFeed = useCallback(async () => {
    setFeedLoading(true)
    try {
      const posts = await agent.SlopFeed.getFeed(1, 20)
      setFeedPosts(posts || [])
      setCurrentPage(1)
      setHasMore((posts?.length ?? 0) === 20)
    } catch (err) {
      console.error('Failed to load feed:', err)
    } finally {
      setFeedLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!loadLibraryFeed) return
    void loadInitialFeed()
  }, [loadInitialFeed, loadLibraryFeed])

  useEffect(() => {
    if (!uploadModalOpen) return
    let cancelled = false
    setFirearmListLoading(true)
    void (async () => {
      try {
        const [fm, cs] = await Promise.all([
          agent.FirearmModels.list({ pageSize: 100 }),
          agent.CartridgeSpecs.list({ pageSize: 80 })
        ])
        if (cancelled) return
        setFirearmModels(fm.items)
        setCartridgeOptions(cs.items.map((c) => ({
          value: c.id,
          label: (c.shortName || c.name || '').trim() || c.id
        })))
      } catch {
        if (!cancelled) { setFirearmModels([]); setCartridgeOptions([]) }
      } finally {
        if (!cancelled) setFirearmListLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [uploadModalOpen])

  const loadMoreFeed = useCallback(async () => {
    const nextPage = currentPage + 1
    setFeedLoading(true)
    try {
      const posts = await agent.SlopFeed.getFeed(nextPage, 20)
      if (posts && posts.length > 0) {
        setFeedPosts((prev) => [...prev, ...posts])
        setCurrentPage(nextPage)
        setHasMore(posts.length === 20)
      } else {
        setHasMore(false)
      }
    } catch (err) {
      console.error('Failed to load more posts:', err)
    } finally {
      setFeedLoading(false)
    }
  }, [currentPage])

  useEffect(() => {
    if (!loadLibraryFeed || !sentinelRef.current || !hasMore || feedLoading) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !feedLoading) {
        void loadMoreFeed()
      }
    })
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [feedLoading, hasMore, loadMoreFeed, loadLibraryFeed])

  useEffect(() => {
    if (!selectedPost) {
      setComments([])
      setNewComment('')
      setReplyingToId(null)
      setReplyText('')
      return
    }
    setCommentsLoading(true)
    agent.SlopFeed.getComments(selectedPost.id)
      .then(setComments)
      .catch(() => setComments([]))
      .finally(() => setCommentsLoading(false))
  }, [selectedPost?.id])

  const resetUploadForm = () => {
    setVideoFile(null)
    setUploadTitle('')
    setUploadDescription('')
    setConsentGiven(false)
    setSelectedFirearmModelId(null)
    setSelectedCartridgeSpecId(null)
    setFirearmSearch('')
    setUploadError(null)
  }

  const handleUpload = async () => {
    setUploadError(null)
    if (!videoFile) { setUploadError('Please select a video file'); return }
    if (!uploadTitle.trim()) { setUploadError('Please enter a title'); return }
    if (!consentGiven) { setUploadError('You must agree to the consent terms'); return }
    if (videoFile.size > MAX_SLOP_VIDEO_BYTES) {
      setUploadError('Video exceeds 150 MB limit')
      return
    }
    setUploadLoading(true)
    try {
      const formData = new FormData()
      formData.append('Video', videoFile)
      formData.append('Title', uploadTitle)
      formData.append('Description', uploadDescription)
      formData.append('ConsentAccepted', 'true')
      formData.append('ConsentText', consentText?.body || '')
      formData.append('ConsentTextVersion', String(consentText?.version || 1))
      if (selectedFirearmModelId) formData.append('FirearmModelId', selectedFirearmModelId)
      if (selectedCartridgeSpecId) formData.append('CartridgeSpecId', selectedCartridgeSpecId)
      await agent.SlopFeed.upload(formData)
      resetUploadForm()
      setUploadModalOpen(false)
      await loadInitialFeed()
    } catch (err: unknown) {
      const status = (err as any)?.response?.status
      const msg = status === 503
        ? 'Video upload is temporarily unavailable.'
        : err instanceof Error ? err.message : 'Failed to upload video'
      setUploadError(msg)
    } finally {
      setUploadLoading(false)
      setUploadProgress(0)
    }
  }

  const handleAddEmbed = async () => {
    setEmbedError(null)
    if (!embedUrl.trim()) { setEmbedError('Please enter an embed URL'); return }
    if (!embedTitle.trim()) { setEmbedError('Please enter a title'); return }
    setEmbedLoading(true)
    try {
      await agent.SlopFeed.addEmbed({
        embedSourceUrl: embedUrl,
        title: embedTitle,
        description: embedDescription
      })
      setEmbedUrl('')
      setEmbedTitle('')
      setEmbedDescription('')
      await loadInitialFeed()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add embed'
      setEmbedError(msg)
    } finally {
      setEmbedLoading(false)
    }
  }

  const handleDeletePost = async (id: string) => {
    setDeleteLoading(true)
    try {
      await agent.SlopFeed.deletePost(id)
      setFeedPosts((prev) => prev.filter((p) => p.id !== id))
      setDeleteConfirmId(null)
      setSelectedPost(null)
    } catch (err) {
      console.error('Failed to delete post:', err)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleToggleLike = async (id: string) => {
    try {
      const result = await agent.SlopFeed.toggleLike(id)
      setFeedPosts((prev) =>
        prev.map((p) =>
          p.id === id
            ? { ...p, likeCount: result.newLikeCount, isLikedByCurrentUser: result.isLiked }
            : p
        )
      )
      if (selectedPost?.id === id) {
        setSelectedPost({
          ...selectedPost,
          likeCount: result.newLikeCount,
          isLikedByCurrentUser: result.isLiked
        })
      }
    } catch (err) {
      console.error('Failed to toggle like:', err)
    }
  }

  const requireAuth = (action: () => void) => {
    if (!isLoggedIn) { history.push('/register'); return }
    action()
  }

  const handleShare = async (post: SlopPostDto) => {
    const url = `${window.location.origin}/slop-feed/${post.id}`
    if (navigator.share) {
      try { await navigator.share({ title: post.title || 'GunSlop Video', url }) } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    }
  }

  const bumpCommentCount = (postId: string, delta: number) => {
    setFeedPosts((prev) =>
      prev.map((p) => p.id === postId ? { ...p, commentCount: Math.max(0, p.commentCount + delta) } : p)
    )
    if (selectedPost?.id === postId) {
      setSelectedPost((p) => p ? { ...p, commentCount: Math.max(0, p.commentCount + delta) } : p)
    }
  }

  const handleAddComment = async (postId: string) => {
    if (!newComment.trim()) return
    setCommentSubmitting(true)
    try {
      const created = await agent.SlopFeed.addComment(postId, newComment.trim())
      setComments((prev) => [...prev, created])
      setNewComment('')
      bumpCommentCount(postId, 1)
    } catch (err) {
      console.error('Failed to add comment:', err)
    } finally {
      setCommentSubmitting(false)
    }
  }

  const handleAddReply = async (postId: string, parentId: number) => {
    if (!replyText.trim()) return
    setCommentSubmitting(true)
    try {
      const created = await agent.SlopFeed.addComment(postId, replyText.trim(), parentId)
      setComments((prev) =>
        prev.map((c) => c.id === parentId ? { ...c, replies: [...c.replies, created] } : c)
      )
      setReplyText('')
      setReplyingToId(null)
      bumpCommentCount(postId, 1)
    } catch (err) {
      console.error('Failed to add reply:', err)
    } finally {
      setCommentSubmitting(false)
    }
  }

  const handleDeleteComment = async (postId: string, commentId: number, parentId?: number) => {
    try {
      await agent.SlopFeed.deleteComment(postId, commentId)
      if (parentId != null) {
        setComments((prev) =>
          prev.map((c) => c.id === parentId ? { ...c, replies: c.replies.filter((r) => r.id !== commentId) } : c)
        )
      } else {
        setComments((prev) => prev.filter((c) => c.id !== commentId))
      }
      bumpCommentCount(postId, -1)
    } catch (err) {
      console.error('Failed to delete comment:', err)
    }
  }

  const closeUploadModal = () => {
    setUploadModalOpen(false)
    setSelectedFirearmModelId(null)
    setSelectedCartridgeSpecId(null)
    setFirearmSearch('')
  }

  return {
    isAdmin,
    isLoggedIn,
    history,
    uploadModalOpen,
    setUploadModalOpen,
    uploadLoading,
    uploadProgress,
    uploadError,
    videoFile,
    setVideoFile,
    uploadTitle,
    setUploadTitle,
    uploadDescription,
    setUploadDescription,
    consentGiven,
    setConsentGiven,
    firearmModels,
    cartridgeOptions,
    firearmListLoading,
    firearmSearch,
    setFirearmSearch,
    selectedFirearmModelId,
    setSelectedFirearmModelId,
    selectedCartridgeSpecId,
    setSelectedCartridgeSpecId,
    embedUrl,
    setEmbedUrl,
    embedTitle,
    setEmbedTitle,
    embedDescription,
    setEmbedDescription,
    embedLoading,
    embedError,
    feedPosts,
    feedLoading,
    hasMore,
    selectedPost,
    setSelectedPost,
    deleteConfirmId,
    setDeleteConfirmId,
    deleteLoading,
    comments,
    commentsLoading,
    newComment,
    setNewComment,
    commentSubmitting,
    replyingToId,
    setReplyingToId,
    replyText,
    setReplyText,
    shareCopied,
    consentText,
    consentLoading,
    sentinelRef,
    handleUpload,
    handleAddEmbed,
    handleDeletePost,
    handleToggleLike,
    requireAuth,
    handleShare,
    handleAddComment,
    handleAddReply,
    handleDeleteComment,
    closeUploadModal
  }
}

export type SlopLibraryFeed = ReturnType<typeof useSlopLibraryFeed>
